import { CancelXIcon } from '@m/alchemy-ui/icons/CancelXIcon';
import { Field } from '@m/alchemy-ui/Field';
import { Popper } from '@m/alchemy-ui/Popper';
import { PopperBox } from '@m/alchemy-ui/PopperBox';
import { FormattedMessage, useIntl } from 'react-intl';
import { Form, Formik, type FormikHelpers } from 'formik';
import { DeleteButton } from '../../people/People.styled';
import { type UserFieldType } from '../types';
import * as Styled from './SearchFilters.styled';

export const TextInputFilter = ({
  id,
  name,
  selectedFilters,
  handleChange,
  userFieldUpdate,
  userFieldOptions,
  setSkip
}: {
  readonly id: string;
  readonly name: string;
  readonly selectedFilters?: string[];
  readonly handleChange: (
    { id, option }: { id: string; option: string },
    userFieldUpdate: (userFieldValue: UserFieldType) => void,
    userFieldOptions: UserFieldType,
    setSkip?: (skip: boolean) => void
  ) => void;
  readonly userFieldUpdate: (userFieldValue: UserFieldType) => void;
  readonly userFieldOptions: UserFieldType;
  readonly setSkip?: (skip: boolean) => void;
}) => {
  const CHARACTER_LIMIT = 2;
  const intl = useIntl();
  const selectedFiltersCount = selectedFilters?.length;

  const handleInputSubmit = (
    { text }: { text: string },
    formik: FormikHelpers<{
      text: string;
    }>
  ) => {
    const { setFieldError } = formik;
    const trimmedText = text.trim();
    const filterAlreadyExist = selectedFilters?.find((filter) => filter.toLowerCase() === trimmedText.toLowerCase());
    if (trimmedText.length > 0 && trimmedText.length < CHARACTER_LIMIT) {
      setFieldError(
        'text',
        intl.formatMessage({
          id: 'enterAtLeastTwoChars',
          defaultMessage: 'Please enter at least two characters'
        })
      );
    } else if (filterAlreadyExist) {
      setFieldError(
        'text',
        intl.formatMessage(
          {
            id: 'filterAlreadyApplied',
            defaultMessage: 'Filter {input} is already applied'
          },
          { input: trimmedText }
        )
      );
    } else {
      formik.resetForm();
      handleChange({ id, option: text }, userFieldUpdate, userFieldOptions, setSkip);
    }
  };

  return (
    <Popper
      trigger={
        <Styled.FilterButton priority="secondary">
          {name}
          {selectedFiltersCount ? (
            <>
              <Styled.FiltersCount>{selectedFiltersCount}</Styled.FiltersCount>
            </>
          ) : (
            <>
              <Styled.FiltersCount>{intl.formatMessage({ id: 'all', defaultMessage: 'All' })}</Styled.FiltersCount>
            </>
          )}
          <Styled.ArrowDown aria-hidden="true" />
        </Styled.FilterButton>
      }
      placement="bottom-start"
      overlay={
        <PopperBox hasArrow maxWidth={250}>
          <Styled.FiltersContainer>
            {selectedFilters?.map((option: string) => (
              <Styled.AddedFilter key={option} data-testid="AddedFilter">
                {option}
                <DeleteButton
                  aria-label={intl.formatMessage(
                    {
                      id: 'deleteFilter',
                      defaultMessage: 'Delete filter {filterName}'
                    },
                    { filterName: option }
                  )}
                  onClick={() => handleChange({ id, option }, userFieldUpdate, userFieldOptions, setSkip)}
                >
                  <CancelXIcon aria-hidden="true" />
                </DeleteButton>
              </Styled.AddedFilter>
            ))}
          </Styled.FiltersContainer>
          <Formik
            initialValues={{
              text: ''
            }}
            onSubmit={handleInputSubmit}
          >
            {({ values, handleChange, errors }) => (
              <Form>
                <Field
                  input={
                    <Styled.StyledInput
                      name="text"
                      placeholder={intl.formatMessage({
                        id: 'keywordToFilter',
                        defaultMessage: 'Enter keyword to filter'
                      })}
                      value={values.text}
                      onChange={handleChange}
                    />
                  }
                  status={errors.text ? { level: 'error', message: errors.text } : undefined}
                />
                <Styled.FilterInputSubmitButton
                  priority="primary"
                  size="small"
                  type="submit"
                  aria-label={intl.formatMessage({
                    id: 'applyFilter',
                    defaultMessage: 'Apply filter'
                  })}
                >
                  <FormattedMessage id="apply" defaultMessage="Apply" />
                </Styled.FilterInputSubmitButton>
              </Form>
            )}
          </Formik>
        </PopperBox>
      }
    />
  );
};
